import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import SignIn from './signin';
import AuthCallback from './auth/AuthCallback';
import MasterAccount from './masteraccount';
import BrandManager from './brandmanager';
import BrandManagerSupport from './BrandManagerSupport';
import BrandSupport from './BrandSupport';
import BrandManagerAccount from './BrandManagerAccount';
import BrandManagerUserManagement from './BrandManagerUserManagement';
import BrandManagerBrandRequests from './BrandManagerBrandRequests';
import BrandManagerPerformance from './BrandManagerPerformance';
import BrandManagerReports from './BrandManagerReports';
import BrandManagerSettings from './BrandManagerSettings';
import AddBrand from './addbrand';
import {
  CLIENT_SUPPORT_HUB_PATH,
  BRAND_SUPPORT_PATH,
} from './supportHubPaths';
import {
  BM_USER_MANAGEMENT_USERS,
  BM_BRAND_REQUESTS,
  BM_ANALYTICS,
  BM_REPORTS,
  BM_SETTINGS,
} from './brandManagerPaths';
import VendorSignup from './VendorSignup';
import LocationsLogisticTemplate from './locationslogistictemplate';
import Signup from './signup';
import ProtectedRoute from './components/ProtectedRoute';
import RequireClientBrandContext from './components/RequireClientBrandContext';
import { UserProvider } from './context/UserContext';
import CanonicalProfileHydrator from './auth/CanonicalProfileHydrator';
import { BrandProvider } from './context/BrandContext';
import authService from './services/authService';
import './App.css';
import './VendorSignup.css';
import './masteraccount.css';
import { maybeSetupWebSocket } from './websocket';
import ForgotPassword from './ForgotPassword';
import ConfirmEmail from './ConfirmEmail';
import PasswordChanged from './PasswordChanged';
import SupportModeBootstrap from './supportMode/SupportModeBootstrap';
import SupportModeBanner from './supportMode/SupportModeBanner';
import './supportMode/SupportModeBanner.css';
import { isImpersonating as checkImpersonating } from './supportMode/supportModeSession';
import { TenantProvider } from './tenant/TenantContext';
import TenantBootstrapGate from './tenant/components/TenantBootstrapGate';
import CapabilityRoute from './tenant/components/CapabilityRoute';
import TenantLandingRedirect from './tenant/components/TenantLandingRedirect';
import ForbiddenPage from './tenant/components/ForbiddenPage';

function Guarded({ children }) {
  return <CapabilityRoute>{children}</CapabilityRoute>;
}

/** Re-check Support Mode on mount so the banner appears after handoff. */
function SupportModeBannerHost() {
  const [active, setActive] = useState(() => checkImpersonating());
  useEffect(() => {
    setActive(checkImpersonating());
  }, []);
  return active ? <SupportModeBanner /> : null;
}

function App() {
  useEffect(() => {
    let isMounted = true;

    const initializeWebSocket = () => {
      // Phase 1: no live V2 WebSocket. maybeSetupWebSocket is a no-op unless
      // REACT_APP_ENABLE_WS=true and REACT_APP_WS_URL is set explicitly.
      if (!authService.isLoggedIn()) {
        return;
      }

      maybeSetupWebSocket(async () => {
        if (!isMounted) {
          return null;
        }
        return authService.getValidAccessToken();
      });
    };

    initializeWebSocket();

    const handleStorageChange = (event) => {
      if (event.key === 'auth_token' || event.key === 'auth_expires_at') {
        initializeWebSocket();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      isMounted = false;
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <UserProvider>
      <BrandProvider>
        <Router>
          <SupportModeBannerHost />
          <Routes>
            <Route path="/" element={<SignIn />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/vendor-signup" element={<VendorSignup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/password-changed" element={<PasswordChanged />} />
            <Route path="/callback" element={<AuthCallback />} />
            <Route path="/support-mode" element={<SupportModeBootstrap />} />

            {/* Authenticated: bootstrap first, then approved product routes */}
            <Route
              element={
                <ProtectedRoute>
                  <TenantProvider>
                    {/* Direct login: canonical /me/profile → shared chrome (header avatar) */}
                    <CanonicalProfileHydrator />
                    <TenantBootstrapGate />
                  </TenantProvider>
                </ProtectedRoute>
              }
            >
              <Route path="/app" element={<TenantLandingRedirect />} />
              <Route path="/app/*" element={<TenantLandingRedirect />} />
              <Route
                path="/dashboard"
                element={<Navigate to="/brand-manager" replace />}
              />
              <Route
                path="/brand-manager"
                element={
                  <Guarded>
                    <BrandManager />
                  </Guarded>
                }
              />
              <Route
                path="/brand-manager/add-brand"
                element={
                  <Guarded>
                    <AddBrand />
                  </Guarded>
                }
              />
              <Route
                path={BM_BRAND_REQUESTS}
                element={
                  <Guarded>
                    <BrandManagerBrandRequests />
                  </Guarded>
                }
              />
              <Route
                path={BM_ANALYTICS}
                element={
                  <Guarded>
                    <BrandManagerPerformance />
                  </Guarded>
                }
              />
              <Route
                path={BM_REPORTS}
                element={
                  <Guarded>
                    <BrandManagerReports />
                  </Guarded>
                }
              />
              <Route
                path={BM_SETTINGS}
                element={
                  <Guarded>
                    <BrandManagerSettings />
                  </Guarded>
                }
              />
              <Route
                path={CLIENT_SUPPORT_HUB_PATH}
                element={
                  <Guarded>
                    <BrandManagerSupport />
                  </Guarded>
                }
              />
              <Route
                path="/brand-manager/account/*"
                element={
                  <Guarded>
                    <BrandManagerAccount />
                  </Guarded>
                }
              />
              <Route
                path="/brand-manager/user-management"
                element={
                  <Guarded>
                    <Navigate to={BM_USER_MANAGEMENT_USERS} replace />
                  </Guarded>
                }
              />
              <Route
                path="/brand-manager/user-management/users"
                element={
                  <Guarded>
                    <BrandManagerUserManagement activeTab="users" />
                  </Guarded>
                }
              />
              <Route
                path="/brand-manager/user-management/roles"
                element={
                  <Guarded>
                    <BrandManagerUserManagement activeTab="roles" />
                  </Guarded>
                }
              />
              {/* Legacy BM /users placeholder → canonical UM */}
              <Route
                path="/brand-manager/users"
                element={
                  <Guarded>
                    <Navigate to={BM_USER_MANAGEMENT_USERS} replace />
                  </Guarded>
                }
              />
              {/* Brand-scoped Support (selected Brand); no Brand → Client hub */}
              <Route
                path={BRAND_SUPPORT_PATH}
                element={
                  <Guarded>
                    <BrandSupport />
                  </Guarded>
                }
              />
              <Route
                path="/llt/:brandId"
                element={
                  <Guarded>
                    <LocationsLogisticTemplate />
                  </Guarded>
                }
              />
              <Route
                path="/master-accounts-dashboard"
                element={
                  <Guarded>
                    <MasterAccount />
                  </Guarded>
                }
              />
              <Route
                path="/master-account/*"
                element={
                  <Guarded>
                    <RequireClientBrandContext>
                      <MasterAccount />
                    </RequireClientBrandContext>
                  </Guarded>
                }
              />              {/* Invented shells from the prior regression — fail closed */}
              <Route
                path="/music-provider"
                element={<ForbiddenPage reason="path-not-in-experience" />}
              />
              <Route
                path="/music-provider/*"
                element={<ForbiddenPage reason="path-not-in-experience" />}
              />
              <Route
                path="/installer"
                element={<ForbiddenPage reason="path-not-in-experience" />}
              />
              <Route
                path="/installer/*"
                element={<ForbiddenPage reason="path-not-in-experience" />}
              />
            </Route>

            <Route path="*" element={<Navigate to="/signin" replace />} />
          </Routes>
        </Router>
      </BrandProvider>
    </UserProvider>
  );
}

export default App;
